/**
 * Security Advisory of a vulnerability case (DEV-134): the live preview.
 *
 * The server renders the advisory from the one template (DEV-138), the core
 * facts, CMDB values and the further fields, and owns the release rules. While
 * the further fields and the notes are edited — or the template in the system
 * settings — the browser renders the same way, so the preview and the gaps
 * follow every keystroke.
 */
import type { MessageDescriptor } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';

import type { AdvisorySource, CaseAdvisory } from '../../lib/types';

const COMMENT = /<!--[\s\S]*?-->/g;
/** The Markdown editor escapes underscores: {{cve\_id}} is the placeholder cve_id. */
const PLACEHOLDER = /\{\{\s*([\w\\]+)\s*\}\}/g;

function placeholderName(key: string): string {
  return key.replaceAll('\\', '').toLowerCase();
}

/** Sources a further field of the advisory may fill or override. */
const EDITABLE: ReadonlySet<AdvisorySource> = new Set(['cmdb', 'field']);

/** Unsaved edits of the advisory. */
export interface AdvisoryDraft {
  values: Readonly<Record<string, string>>;
  notes: string;
}

export interface AdvisoryGap {
  name: string;
  label: string;
  source: AdvisorySource;
}

/** What a template may know about a placeholder. */
export interface PlaceholderInfo {
  label: string;
  source: AdvisorySource;
}

/** One further field of the template, with the value the case supplies. */
export interface AdvisoryField {
  name: string;
  label: string;
  /** From the CMDB; empty for a field only the advisory knows. */
  derived: string;
}

/** The placeholders a template uses outside comments, in order, each once. */
export function placeholderNames(markdown: string): string[] {
  const text = markdown.replace(COMMENT, '');
  return [...new Set(Array.from(text.matchAll(PLACEHOLDER), (match) => placeholderName(match[1] ?? '')))];
}

/**
 * Mirrors `render` in advisory_template.py: one pass, empty notes vanish,
 * comments never count. Unknown placeholders count as gaps of their own name.
 */
export function fillTemplate(
  markdown: string, values: Readonly<Record<string, string>>, known: Readonly<Record<string, PlaceholderInfo>>,
): { text: string; missing: AdvisoryGap[] } {
  const missing = new Map<string, AdvisoryGap>();
  const text = markdown
    .replace(COMMENT, '')
    .replaceAll('\r\n', '\n')
    .replace(PLACEHOLDER, (placeholder, key: string) => {
      const name = placeholderName(key);
      const value = values[name]?.trim();
      if (value) return value;
      const info = known[name];
      if (info?.source === 'notes') return '';
      missing.set(name, { name, label: info?.label ?? name, source: info?.source ?? 'field' });
      return placeholder;
    })
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { text, missing: [...missing.values()] };
}

/** The further fields the template shows, in the order it uses them. */
export function editableFields(advisory: CaseAdvisory): AdvisoryField[] {
  return placeholderNames(advisory.template).flatMap((name) => {
    const field = advisory.fields[name];
    return field && EDITABLE.has(field.source) ? [{ name, label: field.label, derived: field.value }] : [];
  });
}

/** The advisory as it would look with the unsaved edits. */
export function renderAdvisory(advisory: CaseAdvisory, draft: AdvisoryDraft): { text: string; missing: AdvisoryGap[] } {
  const notes = draft.notes.trim();
  const values: Record<string, string> = {
    ...Object.fromEntries(Object.entries(advisory.fields).map(([name, field]) => [name, field.value])),
    ...Object.fromEntries(Object.entries(draft.values).filter(([name, value]) =>
      value.trim() && EDITABLE.has(advisory.fields[name]?.source ?? 'field'))),
    // Advisory content, not UI: stays in the document language.
    additional_notes: notes ? `## Weitere Hinweise\n\n${notes}` : '',
  };
  return fillTemplate(advisory.template, values, advisory.fields);
}

/** The saved state as a draft. */
export function savedDraft(advisory: CaseAdvisory): AdvisoryDraft {
  return { values: advisory.extras, notes: advisory.notes };
}

/** Further fields without empty values, so an emptied field compares equal to an unset one. */
export function filledValues(values: Readonly<Record<string, string>>): Record<string, string> {
  return Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v));
}

export function draftChanged(advisory: CaseAdvisory, draft: AdvisoryDraft): boolean {
  const a = filledValues(advisory.extras);
  const b = filledValues(draft.values);
  return draft.notes.trim() !== advisory.notes
    || Object.keys(a).length !== Object.keys(b).length || Object.entries(a).some(([k, v]) => b[k] !== v);
}

/** Why the advisory cannot be released now, or null. Mirrors the server rules. */
export function releaseBlocker(advisory: CaseAdvisory, dirty: boolean, changeNote: string): string | null {
  if (!advisory.writable) return t`Der Fall ist entfallen.`;
  if (dirty) return t`Erst die Änderungen speichern.`;
  if (advisory.missing.length > 0) return t`Erst alle Platzhalter füllen.`;
  if (advisory.change_log.length > 0 && !changeNote.trim()) return t`Erst eine Änderungsnotiz eingeben.`;
  return null;
}

const SOURCE_HINTS: Readonly<Partial<Record<AdvisorySource, MessageDescriptor>>> = {
  fact: msg`Kernangabe`,
  cmdb: msg`Zusatzfeld, in der CMDB leer`,
  field: msg`Zusatzfeld`,
  table: msg`Bewertungen`,
};

/** "Zusammenfassung (Kernangabe), Danksagung (Zusatzfeld)". */
export function gapList(missing: readonly AdvisoryGap[]): string {
  return missing.map((gap) => `${gap.label} (${t(SOURCE_HINTS[gap.source] ?? SOURCE_HINTS.field!)})`).join(', ');
}

/**
 * Every reason the advisory cannot be released yet, all at once (DEV-103):
 * *missing* is the live gap list of the preview, so it follows the typing.
 */
export function releaseReasons(
  advisory: CaseAdvisory, missing: readonly AdvisoryGap[], dirty: boolean, changeNote: string,
): string[] {
  if (!advisory.writable) return [t`Der Fall ist entfallen.`];
  const reasons: string[] = [];
  if (missing.length > 0) {
    const gaps = gapList(missing);
    reasons.push(t`Platzhalter füllen: ${gaps}`);
  }
  if (dirty) reasons.push(t`Änderungen speichern`);
  if (advisory.change_log.length > 0 && !changeNote.trim()) reasons.push(t`Änderungsnotiz für das Änderungsprotokoll eingeben`);
  return reasons;
}
